<template>
  <section class="horizontal-menu-sticky"
    :class="`has-hero-text-${$theme.name} ${`theme-${$theme.name}`}`">
    <div class="hero-body paddingless hanger">
      <div class="horizontal-menu-container">
        <module-sub-nav :menu-items="visibleMenuItems"
          :menu-items-more="visibleMoreMenuItems"
          @item-click="handleItemClick"
          @custom-action="handleCustomAction">
          <template #menu-item-actions="{ item }">
            <span v-if="item.label === 'Images' && imageCount > 0"
              class="icon breakout-icon-hover is-clickable"
              @click.stop.prevent="handleClickImageBreakOut"
              v-tippy="{ enabled: true }"
              content="Image Breakout">
              <span class="mso mso-16px">open_in_new</span>
            </span>
          </template>
        </module-sub-nav>
      </div>
    </div>
  </section>
</template>

<script>
import ModuleSubNav from '@/components/ModuleSubNav'
import QuoteRoutes from './route-types'
import { QuotingMethodTypes, InvoiceTypes, EventHubTypes, RemarkTypes, ExternalSettingTypes, CommunicationTypes } from '@/enums'
import Guid from '@/components/Guid'
import { createNamespacedHelpers } from 'vuex'
import { CompanySettingService } from '@/services'

const { mapGetters, mapActions } = createNamespacedHelpers('quoteContentCounts')

export default {
  name: 'QuoteSubNav',
  inject: ['$vv'],
  components: {
    ModuleSubNav
  },
  props: {
    value: null,
    insurer: {
      type: Object,
      default: () => null
    },
    ormUnreadMessageCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      showJobNoteRemarks: false
    }
  },
  computed: {
    // One request behind all of these, shared with the Comms tab rather than fetched
    // again there. Held per channel rather than as one total: when the user reads one
    // channel the Comms dot must stay lit if either of the others still has unread.
    ...mapGetters([
      'imageCount',
      'documentCount',
      'smsUnreadCount',
      'whatsAppUnreadCount',
      'emailUnreadCount',
      'commsTotalCount',
      'remarkUnreadCount',
      'imageUnreadCount',
      'documentUnreadCount'
    ]),
    $v() {
      return this.$vv || this.$v
    },
    visibleMenuItems() {
      return this.menuItems
        .filter((item) => item.visible)
        .map((item) => ({
          ...item,
          // Convert badgeClass object to string for ModuleSubNav
          badgeClass: this.convertBadgeClass(item.badgeClass)
        }))
    },
    visibleMoreMenuItems() {
      return this.menuItemsMore
        .filter((item) => item.visible)
        .map((item) => ({
          ...item,
          // Convert badgeClass object to string for ModuleSubNav
          badgeClass: this.convertBadgeClass(item.badgeClass)
        }))
    },
    routeTypes() {
      return QuoteRoutes
    },
    quotingMethodTypes() {
      return QuotingMethodTypes
    },
    enableItemRoutes() {
      return Guid.validGuid(this.value.insurerId) && Guid.validGuid(this.value.vehicleId)
    },
    pnetAudanetMenuText() {
      if (!this.value.isAudaNet) return ''

      const taskType = this.value.audatex?.taskType
      if (taskType) {
        return this.value.isAudaBridge ? `${taskType}` : `/${taskType}`
      }

      return '/AudaNet'
    },
    others() {
      return this.value.others.filter((i) => !i.deleted && i.itemType !== 'MECH')
    },
    invoice() {
      return this.value.invoices.find((i) => i.invoiceType === InvoiceTypes.Quote && !i.deleted)
    },
    excessInvoice() {
      return this.value.invoices.find((i) => (i.invoiceType === InvoiceTypes.Excess || i.invoiceType === InvoiceTypes.OwnerContribution) && !i.deleted)
    },
    unreadCount() {
      return this.smsUnreadCount + this.whatsAppUnreadCount + this.emailUnreadCount
    },
    hasNewImages() {
      // Per-user unread state from QuoteContentLastRead, not the notification feed. The old
      // rule counted only QuoteImageUploadedIncoming - anonymous customer uploads - and
      // was quote-wide, so one person opening the tab cleared it for the whole shop.
      // FQ-3087 asks for per-user, and for a colleague's upload to count too.
      return this.imageUnreadCount > 0
    },
    remarkCount() {
      if (!this.value) return 0
      const activeCount = (remarks, remarkTypeId) => (remarks || []).filter((r) => !r.isDeleted && r.remarkTypeId === remarkTypeId).length
      return (
        activeCount(this.value.remarks, RemarkTypes.Remark) +
        activeCount(this.value.printedRemarks, RemarkTypes.PrintedRemark) +
        (this.showJobNoteRemarks ? activeCount(this.value.jobNoteRemarks, RemarkTypes.JobNote) : 0)
      )
    },
    menuItems() {
      const isNew = this.value.isNew
      return [
        {
          label: 'Header',
          route: { name: this.routeTypes.QuoteHeader.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: false,
          visible: true,
          badgeClass: {
            'has-badge-danger has-badge-small has-badge-rounded':
              this.$v.estimateJobStartEnd.$error ||
              this.$v.quoteHeaderGroup.$error ||
              this.$v.ormHeaderGroup.$error ||
              this.$v.audaNetHeaderGroup.$error ||
              this.$v.ormSummaryGroup.$error
          },
          badge:
            this.$v.estimateJobStartEnd.$error ||
            this.$v.quoteHeaderGroup.$error ||
            this.$v.ormHeaderGroup.$error ||
            this.$v.audaNetHeaderGroup.$error ||
            this.$v.ormSummaryGroup.$error
              ? ''
              : null
        },
        {
          label: 'Quoting',
          route: { name: this.routeTypes.QuoteBuilder.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: !this.enableItemRoutes || isNew,
          visible: true,
          badgeClass: { 'has-badge-rounded has-badge-medium has-badge-danger': true },
          badge: null
        },
        {
          label: 'Parts',
          route: { name: this.routeTypes.QuotePartsControl.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: !this.enableItemRoutes || isNew,
          visible: this.$company.setting.partsControlEnabled,
          badgeClass: { 'has-badge-rounded has-badge-medium has-badge-danger': true },
          badge: null
        },
        {
          label: 'Images',
          route: { name: this.routeTypes.QuoteImages.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: true,
          badgeClass: { 'has-badge-info has-badge-rounded has-badge-medium': this.imageCount },
          badge: this.imageCount ? this.imageCount : null,
          hasNewContent: this.hasNewImages
        },
        {
          label: 'Assessments',
          route: { name: this.routeTypes.AuthoriseView.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: !this.enableItemRoutes || isNew,
          visible: this.value.isAssessmentEnabled,
          badgeClass: { 'has-badge-rounded has-badge-medium has-badge-danger': true },
          badge: null
        },
        {
          label: (this.value.isAudaBridge) ? this.pnetAudanetMenuText : `PNET${this.pnetAudanetMenuText}`,
          route: { name: this.routeTypes.QuotePnetAudanet.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: this.value.audatex || this.value.pnet
        },
        {
          label: 'ORM',
          route: { name: this.routeTypes.QuoteOrm.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: this.value.orm || false,
          badgeClass: { 'has-badge-rounded has-badge-medium has-badge-info': this.ormUnreadMessageCount > 0 },
          badge: this.ormUnreadMessageCount > 0 ? this.ormUnreadMessageCount : null,
          hasNewContent: this.ormUnreadMessageCount > 0
        },
        {
          label: 'ARNIE',
          route: { name: this.routeTypes.QuoteArnie.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: this.value.arnieCount
        },
        {
          label: 'EstImage',
          route: { name: this.routeTypes.QuoteEstImage.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: this.insurer?.estimage,
          badgeClass: { 'has-badge-rounded has-badge-medium has-badge-danger': true },
          badge: null
        },
        {
          label: 'Summary',
          route: { name: this.routeTypes.QuoteSummary.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: true,
          badgeClass: null, //{ 'has-badge-danger has-badge-small has-badge-rounded': this.$v.ormSummaryGroup.$error || this.$v.estimateJobStartEnd.$error },
          badge: null //this.$v.ormSummaryGroup.$error || this.$v.estimateJobStartEnd.$error ? '' : null
        },
        {
          label: 'Invoice',
          route: {
            name: this.value.isAssessmentEnabled ? this.routeTypes.QuoteAssessmentInvoice.name : this.routeTypes.QuoteInvoice.name,
            params: { quoteId: this.$route.params.quoteId },
            query: this.$route.query
          },
          disabled: !this.enableItemRoutes || isNew,
          visible: true,
          //badgeClass: { 'has-badge-rounded has-badge-medium has-badge-icon-check': !!(this.invoice) },
          badgeClass: function () {
            const classes = this.getInvoiceBadgeClass('Invoice')
            return {
              'has-badge-rounded has-badge-small has-badge-icon-check': !!(this.invoice),
              ...classes
            }
          },
          badge: this.invoice ? '✓' : null
        },
        {
          label: 'Excess',
          route: { name: this.routeTypes.QuoteExcessInvoice.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: !this.enableItemRoutes || isNew,
          visible: true,
          badgeClass: function () {
            const classes = this.getInvoiceBadgeClass('Excess')
            return {
              'has-badge-rounded has-badge-small has-badge-icon-check': !!(this.excessInvoice),
              ...classes
            }
          },
          badge: this.excessInvoice ? '✓' : null
        },
        {
          label: 'Docs',
          route: { name: this.routeTypes.QuoteDocuments.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: true,
          badgeClass: { 'has-badge-info has-badge-rounded has-badge-medium': this.documentCount },
          badge: this.documentCount ? this.documentCount : null,
          hasNewContent: this.documentUnreadCount > 0
        },
        {
          label: 'Comms',
          route: { name: this.routeTypes.QuoteCommunications.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: true,
          badgeClass: { 'has-badge-info has-badge-rounded has-badge-medium': this.commsTotalCount },
          badge: this.commsTotalCount ? this.commsTotalCount : null,
          hasNewContent: this.unreadCount > 0
        },
        {
          label: 'Remarks',
          route: { name: this.routeTypes.QuoteRemarks.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: true,
          badgeClass: { 'has-badge-info has-badge-rounded has-badge-medium': this.remarkCount },
          badge: this.remarkCount ? this.remarkCount : null,
          hasNewContent: this.remarkUnreadCount > 0
        },
        {
          label: 'Rates & Markups',
          route: { name: this.routeTypes.QuoteRatesMarkups.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: true
        },
        {
          label: 'Annotations',
          route: { name: this.routeTypes.QuoteAnnotations.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: true
        },
        {
          label: 'Other Labour',
          route: { name: this.routeTypes.QuoteOtherLabour.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: !this.enableItemRoutes || isNew,
          visible: !!this.others.length,
          badgeClass: { 'has-badge-info has-badge-rounded has-badge-medium': this.others.length },
          badge: this.others.length ? this.others.length : null
        },
        {
          label: 'RepX',
          route: { name: this.routeTypes.QuoteRepx.name, params: { quoteId: this.$route.params.quoteId }, query: this.$route.query },
          disabled: isNew,
          visible: this.value.repx
        }
      ]
    },
    menuItemsMore() {
      // const isNew = this.value.isNew
      return [

      ]
    }
  },
  watch: {
    // Switching to a supplementary keeps this component alive - the route name is the
    // same and only the quoteId param changes, so created() does not run again. Starting
    // the counts there instead would leave them describing the quote the user left, with
    // no poll to correct it. Watching the prop also covers the notification bell, which
    // pushes straight from one quote to another.
    'value.quoteId': {
      handler(quoteId) {
        if (!quoteId) return
        this.startContentCounts(quoteId)
      },
      immediate: true
    }
  },
  async created() {
    this.getPlanningPlusActiveStatus()
  },
  mounted() {
    // Counts changed underneath us - a refresh is cheaper to reason about than
    // trusting a number passed through an event.
    this.$eventHub.$on(EventHubTypes.ImageCountChanged, () => this.refreshContentCounts())
    this.$eventHub.$on(EventHubTypes.DocumentCountChanged, () => this.refreshContentCounts())
    // Raised by QuoteView when the SMS modal closes, which may or may not have sent
    // anything - it carries no count, it just asks for a re-read.
    this.$eventHub.$on(EventHubTypes.CommsCountChanged, () => this.refreshContentCounts())
    // A sender that already knows the new total passes it; one that only knows
    // something changed passes nothing, and we go and read it.
    // Each tab clears its own indicator on open rather than waiting out the poll.
    this.$eventHub.$on(EventHubTypes.RemarksRead, () => this.markContentRead('remarks'))
    this.$eventHub.$on(EventHubTypes.ImagesRead, () => this.markContentRead('images'))
    this.$eventHub.$on(EventHubTypes.DocumentsRead, () => this.markContentRead('documents'))
    // Comms clears one channel at a time, so the payload names which one.
    this.$eventHub.$on(EventHubTypes.CommsRead, (channel) => {
      if (channel === CommunicationTypes.SMS) this.markContentRead('sms')
      else if (channel === CommunicationTypes.WhatsApp) this.markContentRead('whatsApp')
      else if (channel === CommunicationTypes.Email) this.markContentRead('email')
    })
  },
  beforeDestroy() {
    this.$eventHub.$off(EventHubTypes.ImageCountChanged)
    this.$eventHub.$off(EventHubTypes.DocumentCountChanged)
    this.$eventHub.$off(EventHubTypes.CommsCountChanged)
    this.$eventHub.$off(EventHubTypes.RemarksRead)
    this.$eventHub.$off(EventHubTypes.ImagesRead)
    this.$eventHub.$off(EventHubTypes.DocumentsRead)
    this.$eventHub.$off(EventHubTypes.CommsRead)
    this.stopContentCounts()
  },
  methods: {
    convertBadgeClass(badgeClass) {
      if (!badgeClass) return ''

      const resolved = typeof badgeClass === 'function' ? badgeClass.call(this) : badgeClass

      if (typeof resolved === 'string') return resolved

      if (Array.isArray(resolved)) {
        const parts = []
        resolved.forEach((entry) => {
          if (!entry) return
          if (typeof entry === 'string') {
            parts.push(entry)
          } else if (typeof entry === 'object') {
            parts.push(
              Object.keys(entry)
                .filter((key) => entry[key])
                .join(' ')
            )
          }
        })
        return parts.filter(Boolean).join(' ').trim()
      }

      if (typeof resolved === 'object') {
        return Object.keys(resolved)
          .filter((key) => resolved[key])
          .join(' ')
      }

      return ''
    },
    handleItemClick(item) {
      this.goToRoute(item.route, item.disabled)
    },
    handleCustomAction(payload) {
      // Handle custom actions emitted from ModuleSubNav slots
      const { item, actionType, args } = payload
      this.$emit('custom-action', { item, actionType, args })
    },
    goToRoute(route, isDisabled) {
      if (isDisabled) {
        this.$toast.open({
          message: 'Please create the quote to make changes',
          type: 'is-warning',
          position: 'is-top'
        })
        return
      }
      this.$router.push(route)
    },
    // The counts all arrive together from the store; these are its controls.
    ...mapActions({
      startContentCounts: 'start',
      stopContentCounts: 'stop',
      refreshContentCounts: 'refresh',
      markContentRead: 'markRead'
    }),
    async getPlanningPlusActiveStatus() {
      const externalSettings = await CompanySettingService.getCompanyExternalSettings()
      const planningPlusExternalSetting = externalSettings.find((i) => i.settingName === ExternalSettingTypes.SETTING_PLANNINGPLUS)
      this.showJobNoteRemarks = !!parseInt(planningPlusExternalSetting?.property5) // Active property
    },
    handleClickImageBreakOut() {
      const baseUri = process.env.VUE_APP_BASE_URI === '/' ? process.env.VUE_APP_BASE_URI : `${process.env.VUE_APP_BASE_URI}/`
      const newWindow = window.open(`${baseUri}quotes/${this.value.quoteId}/imagesbreakout?uninterruptedview=true`, 'Imageviewer', {
        width: 1600,
        height: 900,
        left: 0,
        top: 0,
        fullscreen: 'yes',
        title: 'Image Breakout'
      })
      if (newWindow) {
        const timer = setInterval(() => {
          if (newWindow.closed) {
            clearInterval(timer)
            if (this.$router.currentRoute.name == QuoteRoutes.QuoteImages.name) {
              this.$emit('reload-quote-images')
            }
          }
        }, 500)
      }
    },
    getInvoiceBadgeClass(type) {
      let classes = {}

      if (type === 'Invoice') {
        if (this.$v.entity.invoices.$error) {
          let errors = Object.values(this.$v.entity['invoices'].$each.$iter).filter((r) => {
            return r.$anyError
          })
          errors.forEach((item) => {
            if (item.$model.invoiceType === InvoiceTypes.Quote) {
              classes['has-badge-danger'] = true
            }
          })
        } else if (this.invoice && this.invoice.isNew) {
          classes['has-badge-gold'] = true
        } else {
          classes['has-badge-success'] = true
        }
      }
      if (type === 'Excess') {
        if (this.$v.entity.invoices.$error) {
          let errors = Object.values(this.$v.entity['invoices'].$each.$iter).filter((r) => {
            return r.$anyError
          })
          errors.forEach((item) => {
            if (item.$model.invoiceType === InvoiceTypes.Excess || item.$model.invoiceType === InvoiceTypes.OwnerContribution) {
              classes['has-badge-danger'] = true
            }
          })
        } else if (this.excessInvoice && this.excessInvoice.isNew) {
          classes['has-badge-gold'] = true
        } else {
          classes['has-badge-success'] = true
        }
      }
      return classes
    }
  }
}
</script>

<style lang="scss" scoped>
.horizontal-menu-sticky {
  position: sticky;
  top: 64px;
  z-index: 30;
  padding: 0 0.75rem 0.75rem 0.75rem;
  // overflow-x: auto;
  // background-color: #f0f2f4; //#fff;
}

.horizontal-menu-container {
  width: 100%;
  padding: 0 0.75rem 0.5rem 0.75rem;
  position: relative;
  min-width: max-content;

  ::v-deep [class*='has-badge-'] {
    position: relative;
    z-index: 25;

    &::after {
      position: absolute;
      z-index: 30 !important;
      pointer-events: none;
    }
  }

  ::v-deep .has-badge-medium::after {
    min-height: 1.15em;
    font-size: 0.85em;
    margin-left: 0.25em;
    z-index: 30 !important;
  }

  ::v-deep .has-badge-small::after,
  ::v-deep .has-badge-medium::after,
  ::v-deep .has-badge-large::after,
  ::v-deep .has-badge-info::after,
  ::v-deep .has-badge-success::after,
  ::v-deep .has-badge-warning::after,
  ::v-deep .has-badge-danger::after,
  ::v-deep .has-badge-rounded::after,
  ::v-deep .has-badge-icon-check::after {
    z-index: 30 !important;
    position: absolute !important;
  }
}

@media screen and (max-width: 768px) {
  .horizontal-menu-sticky {
    // overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.hanger {
  background-color: var(--background-color);
}

</style>